import localforage from 'localforage';
import { v4 as uuidv4 } from 'uuid';
import logger from '~/services/logger';
import { type FeedEntry, type Feed } from '../layout';
import { filterFeedEntriesByUniqueId } from '../layout/layout';

export enum RemoteEventType {
  Started = 'com.applicaster.video.started.v1',
  Progress = 'com.applicaster.video.progress.v1',
  Paused = 'com.applicaster.video.paused.v1',
  Stopped = 'com.applicaster.video.stopped.v1',
  VideoInterval = 'com.applicaster.video.interval.v1',
}

export enum RemoteEventStatus {
  VideoStarted = 'VIDEO_STARTED',
  VideoStopped = 'VIDEO_STOPPED',
  Watching = 'WATCHING',
  Completed = 'COMPLETED',
  VideoInterval = 'INTERVAL_COMPLETED',
}

const statusFromRemoteEvent = (
  eventType: RemoteEventType
): RemoteEventStatus => {
  switch (eventType) {
    case RemoteEventType.Started:
      return RemoteEventStatus.VideoStarted;
    case RemoteEventType.Progress:
      return RemoteEventStatus.Watching;
    case RemoteEventType.Paused:
    case RemoteEventType.Stopped:
      return RemoteEventStatus.VideoStopped;
    case RemoteEventType.VideoInterval:
      return RemoteEventStatus.VideoInterval;
    default:
      return RemoteEventStatus.Completed;
  }
};

const defaultLogMessage = 'Error: Can not create client data';

export async function updateContinueWatchingTime(
  videoId: string,
  secondsFromStart: string,
  progress: number,
  sessionStartAt: number,
  extraCWData: any,
  item?: any
) {
  try {
    const payload = {
      videoId,
      secondsFromStart,
      progress,
      sessionStartAt,
      item,
      extraCWData,
    };

    const response = await fetch(`/api/add-continue-watching`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return await response.json();
  } catch (error: any) {
    logger.info(
      `Failed to update continue watching for: ${videoId} Error: ${error.message}`
    );
  }
}

const addSubject = (
  secondsFromStart: string,
  progress: number,
  videoId: string | number
) => {
  if (!secondsFromStart || (!progress && progress !== 0) || !videoId) {
    throw Error(
      `${defaultLogMessage} addSubject: Check time, progress, videoId. Item(s) not exist`
    );
  }

  return `Watched video segment ${secondsFromStart} progress: ${progress} ${videoId}`;
};

export const toNumberWithDefaultZero = (value: any) => {
  const numberValue = Number(value);
  return isNaN(numberValue) ? 0 : numberValue;
};

export const toNumberWithDefault = (
  defaultValue: number,
  value: any
): number => {
  const numberValue = Number(value);
  return isNaN(numberValue) ? defaultValue : numberValue;
};

const addSecondsFromStart = (secondsFromStart: number) => {
  if (!secondsFromStart && secondsFromStart !== 0) {
    throw Error(
      `${defaultLogMessage} addSecondsFromStart: Check secondsFromStart. Item not exist`
    );
  }

  return secondsFromStart.toString();
};

export const prepareCWEventPayload = ({
  item,
  eventType,
  videoId,
  userIdentifier,
  progress,
  secondsFromStart,
  sessionStartAt,
  source,
  extraCWData,
}: {
  item: any;
  eventType: RemoteEventType;
  videoId: string;
  userIdentifier: string;
  progress: number;
  secondsFromStart?: string;
  sessionStartAt?: number;
  source?: string;
  extraCWData?: any;
}) => {
  const time = new Date().toISOString();
  const currentResumeTime =
    secondsFromStart || addSecondsFromStart(item?.extensions?.resumeTime);
  const payload = {
    specversion: '1.0',
    datacontenttype: 'application/json',
    data: {
      userIdentifier,
      videoId,
      progress,
      status: statusFromRemoteEvent(eventType),
      secondsFromStart,
      sessionStartAt,
      ...extraCWData,
    },
    type: eventType,
    source,
    subject: addSubject(currentResumeTime, progress, videoId),
    id: uuidv4(),
    time,
  };

  return payload;
};

export async function sendRemoteEvent({
  item,
  videoId,
  eventType,
  progress,
  secondsFromStart,
  sessionStartAt,
  extraCWData,
}: {
  item: any;
  videoId: string;
  eventType: RemoteEventType;
  progress: number;
  secondsFromStart: string;
  sessionStartAt: number;
  extraCWData: any;
}) {
  try {
    const payload = {
      item,
      eventType,
      videoId,
      progress,
      secondsFromStart,
      sessionStartAt,
      extraCWData,
    };

    await updateCWEntriesIndexedDB({
      CWEntry: payload.item,
      progress,
    });

    await fetch(`/api/cw-remote-events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (error: any) {
    logger.info(
      `Failed to update continue watching for: ${videoId} Error: ${error.message}`
    );
  }
}

async function updateCWEntriesIndexedDB({
  CWEntry,
  progress,
}: {
  CWEntry: FeedEntry;
  progress: number;
}) {
  try {
    const videoId = CWEntry?.id;

    if (!videoId || !CWEntry._webLink) return;

    const APP_VERSION_UUID: string = window.versionInfo?.APP_VERSION_UUID;
    const CW_STORAGE_KEY = `continue-watching-${APP_VERSION_UUID}`;

    let CWFeed = (await localforage.getItem(CW_STORAGE_KEY)) as Feed;

    const isEntryExist = (CWFeed?.entry || []).some(
      (entry: FeedEntry) => entry.id === videoId
    );

    if (!isEntryExist) CWFeed.entry = [CWEntry, ...CWFeed.entry];

    CWFeed.entry = CWFeed.entry.sort((a: FeedEntry, b: FeedEntry) => {
      if (a.id === videoId) return -1;
      if (b.id === videoId) return 1;
      return 0;
    });

    const updatedCWEntries = updateCWEntryProgress({
      CWEntries: CWFeed?.entry || [],
      videoId,
      progress,
    });

    await localforage.setItem(CW_STORAGE_KEY, {
      ...CWFeed,
      entry: updatedCWEntries,
    });
  } catch (error: any) {
    logger.info(`Failed to update CW entries: ${error.message}`);
  }
}

export function updateCWEntryProgress({
  CWEntries,
  videoId,
  progress,
}: {
  CWEntries: any;
  videoId: string;
  progress: number;
}): FeedEntry[] {
  try {
    if (!videoId || isNaN(progress)) return CWEntries;

    const updatedCWEntries: FeedEntry[] = CWEntries.map((entry: FeedEntry) => {
      if (entry.id === videoId) {
        return {
          ...entry,
          extensions: {
            ...entry.extensions,
            progress,
          },
        };
      }
      return entry;
    });

    return filterFeedEntriesByUniqueId(updatedCWEntries);
  } catch (error: any) {
    logger.info(`Failed to update CW entry progress: ${error.message}`);
    return CWEntries;
  }
}
